import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthProvider";
import {
  fetchInvoiceDetail,
  fetchInvoices,
  updateInvoiceStatus,
} from "../services/invoiceService";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("de-CH", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const formatAmount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0.00 CHF";
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const statusLabels = {
  open: "Offen",
  paid: "Bezahlt",
};

const buildQrDocument = (invoice) => {
  if (!invoice) return "";
  return `<!doctype html>
  <html lang="de">
    <head>
      <meta charset="utf-8" />
      <title>${invoice.invoice_number} QR</title>
      <style>
        body {
          margin: 0;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f5f4;
          font-family: Arial, Helvetica, sans-serif;
          color: #0f4a3f;
        }
        .shell {
          background: white;
          border-radius: 24px;
          padding: 24px;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);
          text-align: center;
        }
        h1 {
          margin: 0 0 8px;
          font-size: 24px;
        }
        p {
          margin: 0 0 20px;
          color: #5c7570;
        }
        img {
          width: min(90vw, 720px);
          height: auto;
          display: block;
        }
      </style>
    </head>
    <body>
      <div class="shell">
        <h1>${invoice.invoice_number}</h1>
        <p>Swiss QR-Bill</p>
        ${invoice.qr_svg_data_uri ? `<img src="${invoice.qr_svg_data_uri}" alt="QR Code ${invoice.invoice_number}" />` : "<p>Kein QR-Code verfügbar.</p>"}
      </div>
    </body>
  </html>`;
};

function StatusBadge({ status }) {
  return (
    <span className={`invoice-pill invoice-pill-${status || "open"}`}>
      {statusLabels[status] ?? status ?? "Unbekannt"}
    </span>
  );
}

export default function RechnungenPage() {
  const { fetchWithAuth } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchInvoices(fetchWithAuth);
      setInvoices(rows);
    } catch (err) {
      setError(err.message || "Rechnungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  const loadInvoiceDetail = useCallback(
    async (invoiceId) => {
      if (!invoiceId) {
        return;
      }
      if (invoiceDetails[invoiceId]) {
        return;
      }
      setDetailLoading(true);
      setError(null);
      try {
        const detail = await fetchInvoiceDetail(fetchWithAuth, invoiceId);
        setInvoiceDetails((current) => ({ ...current, [invoiceId]: detail }));
      } catch (err) {
        setError(err.message || "Rechnungsdetails konnten nicht geladen werden.");
      } finally {
        setDetailLoading(false);
      }
    },
    [fetchWithAuth, invoiceDetails]
  );

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    if (!selectedInvoiceId) {
      return;
    }
    loadInvoiceDetail(selectedInvoiceId);
  }, [loadInvoiceDetail, selectedInvoiceId]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const haystack = [
        invoice.invoice_number,
        invoice.driver_name,
        invoice.driver_user_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = haystack.includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === "all" || invoice.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [invoices, searchTerm, statusFilter]);

  const handleStatusChange = async (status) => {
    if (!selectedInvoiceId) return;
    try {
      setError(null);
      const updated = await updateInvoiceStatus(fetchWithAuth, selectedInvoiceId, status);
      setInvoiceDetails((current) => ({ ...current, [selectedInvoiceId]: updated }));
      setInvoices((current) =>
        current.map((invoice) =>
          invoice.invoice_id === selectedInvoiceId
            ? {
                ...invoice,
                status: updated.status,
                paid_at: updated.paid_at,
              }
            : invoice
        )
      );
    } catch (err) {
      setError(err.message || "Rechnungsstatus konnte nicht aktualisiert werden.");
    }
  };

  const openInvoiceInNewTab = (invoice) => {
    if (!invoice) return;
    const html = buildQrDocument(invoice);
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const popup = window.open(url, "_blank", "noopener,noreferrer");
    if (!popup) {
      URL.revokeObjectURL(url);
      setError("Das Rechnungsfenster konnte nicht geöffnet werden.");
      return;
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  const toggleInvoice = (invoiceId) => {
    if (selectedInvoiceId === invoiceId) {
      setSelectedInvoiceId(null);
      return;
    }
    setSelectedInvoiceId(invoiceId);
  };

  return (
    <section className="portal-section invoice-panel">
      <header className="car-top">
        <div>
          <p className="car-eyebrow">Auszahlungen</p>
          <h1 className="portal-title">Rechnungen</h1>
          <p className="portal-desc">
            Prüfe alle gestellten Rechnungen, öffne Details und markiere Zahlungen als offen oder bezahlt.
          </p>
        </div>
        <div className="car-top-actions">
          <input
            className="car-search"
            placeholder="Suche nach Rechnungsnummer oder Fahrer"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <select
            className="car-filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="all">Alle Status</option>
            <option value="open">Offen</option>
            <option value="paid">Bezahlt</option>
          </select>
          <button className="car-refresh" onClick={loadInvoices} disabled={loading}>
            Neu laden
          </button>
        </div>
      </header>

      {error ? <div className="car-alert error">{error}</div> : null}

      <div className="invoice-list">
        <div className="invoice-table-top">
          <span>Rechnung</span>
          <span>Fahrer</span>
          <span>Zeitraum</span>
          <span>Betrag</span>
          <span>Status</span>
          <span>Bezahlt</span>
        </div>
        {loading ? (
          <div className="car-empty">Rechnungen werden geladen…</div>
        ) : filteredInvoices.length === 0 ? (
          <div className="car-empty">Keine Rechnungen gefunden.</div>
        ) : (
          filteredInvoices.map((invoice) => {
            const isOpen = selectedInvoiceId === invoice.invoice_id;
            const detail = invoiceDetails[invoice.invoice_id];
            return (
              <div key={invoice.invoice_id} className="invoice-entry">
                <button
                  type="button"
                  className={`invoice-row${isOpen ? " active" : ""}`}
                  onClick={() => toggleInvoice(invoice.invoice_id)}
                >
                  <div>
                    <div className="car-strong">{invoice.invoice_number}</div>
                    <div className="car-muted">{formatDate(invoice.invoice_date)}</div>
                  </div>
                  <div>
                    <div className="car-strong">{invoice.driver_name || "Unbekannt"}</div>
                    <div className="car-muted">{invoice.driver_user_id}</div>
                  </div>
                  <div className="car-muted">
                    {formatDate(invoice.service_period_start)} bis {formatDate(invoice.service_period_end)}
                  </div>
                  <div className="car-strong">{formatAmount(invoice.total_amount_chf)}</div>
                  <div><StatusBadge status={invoice.status} /></div>
                  <div className="car-muted">{formatDate(invoice.paid_at)}</div>
                </button>

                {isOpen ? (
                  <div className="invoice-inline-detail">
                    {detailLoading && !detail ? (
                      <div className="car-empty">Rechnungsdetails werden geladen…</div>
                    ) : !detail ? (
                      <div className="car-empty">Rechnungsdetails konnten nicht geladen werden.</div>
                    ) : (
                      <>
                        <div className="invoice-detail-top">
                          <div>
                            <h2 className="invoice-title">{detail.invoice_number}</h2>
                            <p className="car-muted">
                              Eingereicht am {formatDate(detail.submitted_at)} · Status {statusLabels[detail.status] ?? detail.status}
                            </p>
                          </div>
                          <div className="invoice-actions">
                            <button
                              type="button"
                              className="btn btn-dl"
                              onClick={() => openInvoiceInNewTab(detail)}
                            >
                              In neuem Tab öffnen
                            </button>
                            <button
                              type="button"
                              className="btn btn-dl"
                              onClick={() => handleStatusChange("open")}
                            >
                              Als offen markieren
                            </button>
                            <button
                              type="button"
                              className="btn btn-approve"
                              onClick={() => handleStatusChange("paid")}
                            >
                              Als bezahlt markieren
                            </button>
                          </div>
                        </div>

                        <div className="invoice-summary-grid">
                          <div className="settings-row">
                            <div className="car-strong">Zahlungsempfänger</div>
                            <div>{detail.driver?.account_holder}</div>
                            <div>{detail.driver?.street} {detail.driver?.house_number}</div>
                            <div>{detail.driver?.postal_code} {detail.driver?.city}</div>
                            <div>{detail.driver?.iban}</div>
                          </div>
                          <div className="settings-row invoice-payment-card">
                            <div className="car-strong">Zahlungsdetails</div>
                            {detail.qr_svg_data_uri ? (
                              <div className="invoice-qr-panel">
                                <img
                                  src={detail.qr_svg_data_uri}
                                  alt={`QR Code ${detail.invoice_number}`}
                                  className="invoice-qr-image"
                                />
                              </div>
                            ) : null}
                            <div className="invoice-payment-details">
                              <div className="invoice-payment-row">
                                <span className="car-muted">Zahlungspflichtige Partei</span>
                                <strong>{detail.platform?.name || "—"}</strong>
                              </div>
                              <div className="invoice-payment-row">
                                <span className="car-muted">Adresse</span>
                                <strong>
                                  {detail.platform?.street || "—"} {detail.platform?.house_number || ""}
                                  <br />
                                  {detail.platform?.postal_code || "—"} {detail.platform?.city || ""}
                                </strong>
                              </div>
                              <div className="invoice-payment-row">
                                <span className="car-muted">IBAN</span>
                                <strong>{detail.platform?.iban || "—"}</strong>
                              </div>
                              <div className="invoice-payment-row">
                                <span className="car-muted">Total</span>
                                <strong>{formatAmount(detail.total_amount_chf)}</strong>
                              </div>
                              <div className="invoice-payment-row">
                                <span className="car-muted">QR-Nachricht</span>
                                <strong>{detail.invoice_number}</strong>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="invoice-facts">
                          <div><span>Rechnungsdatum</span><strong>{formatDate(detail.invoice_date)}</strong></div>
                          <div><span>Leistungszeitraum</span><strong>{formatDate(detail.service_period_start)} bis {formatDate(detail.service_period_end)}</strong></div>
                          <div><span>Total</span><strong>{formatAmount(detail.total_amount_chf)}</strong></div>
                          <div><span>Bezahlt am</span><strong>{formatDate(detail.paid_at)}</strong></div>
                        </div>

                        <div className="invoice-items">
                          <div className="invoice-items-top">
                            <span>Beschreibung</span>
                            <span>Ende</span>
                            <span>Betrag</span>
                          </div>
                          {detail.items.map((item) => (
                            <div className="invoice-item-row" key={item.path_id}>
                              <div>
                                <div className="car-strong">{item.description}</div>
                                <div className="car-muted">{item.number_plate}</div>
                              </div>
                              <div className="car-muted">{formatDate(item.drive_ended_at)}</div>
                              <div className="car-strong">{formatAmount(item.amount_chf)}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}
